import { DeleteOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { Badge, Button } from 'antd';
import Table, { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React from 'react';
import { defaultTablePagination } from '../../../config/constants';
import { defaultFormatWithTime } from '../../../config/constants';
import { documents, translations } from '../../../config/translations';
import { ActionType, ModuleName } from '../../../core/models/enum';
import { authorizeAction } from '../../../helpers/CheckPermissionHelper';
import { formatDate } from '../../../helpers/DateHelper';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { downloadFile, openFileInNewTab } from '../../../helpers/FileHelper';
import {
    getCheckboxFilter,
    getDateFilter,
    getDatePickerFilter,
    getDefaultFilter,
    getKeyFilter,
    getSearchFilter,
} from '../../../helpers/FilterHelper';
import { showConfirm } from '../../../helpers/NotificationHelper';
import { getDateComparer, getDefaultComparer } from '../../../helpers/SortHelper';
import { getTableLocale } from '../../../helpers/TableHelper';
import {
    CodebooksClient,
    DocumentTypeEnum,
    SelectOptionVm,
    VerificationsClient,
    VerificationStatusEnum,
    VerificationVm,
} from '../../../utils/api';
import { Props } from './index';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import {
    applySearchParams,
    getFilterParamsString,
    getFiltersParams,
    getPagingSortingParams,
} from '../../../helpers/SearchParamsHelper';
import { Link } from 'react-router-dom';
interface State {
    allowUpdate: boolean;
    allowDelete: boolean;
    statuses: SelectOptionVm[];
    totalItems?: number;
    loading?: boolean;
}

class VerificationTable extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            allowUpdate: authorizeAction(
                props.userProfile,
                ModuleName.Verifications,
                ActionType.Update,
                undefined,
                undefined,
                undefined
            ),
            allowDelete: authorizeAction(
                props.userProfile,
                ModuleName.Verifications,
                ActionType.Delete,
                undefined,
                undefined,
                undefined
            ),
            statuses: [],
        };
    }

    public componentDidMount = () => {
        this.getVerificationStatuses();
    };

    private openAttachmentInNewTab = async (id: number) => {
        const client = new VerificationsClient();
        const result = await client.downloadFile(id);

        openFileInNewTab(result);
    };

    private downloadAttachment = async (id: number) => {
        const client = new VerificationsClient();
        const result = await client.downloadFile(id);

        downloadFile(result);
    };

    private getVerificationStatuses = async () => {
        this.setState({
            statuses: await new CodebooksClient().getByCodebookName('VerificationStatus'),
        });
    };

    private handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<VerificationVm> | SorterResult<VerificationVm>[] = {}
    ) => {
        const {
            history,
            location: { search },
        } = this.props;

        const currSearchParams = new URLSearchParams(search);
        const tableSearchParams = this.createTableSearchParams(pagination, filters, sorter);
        const newSearchParams = applySearchParams(currSearchParams, tableSearchParams);

        history.push({
            pathname: history.location.pathname,
            search: newSearchParams.toString(),
        });
    };

    private createTableSearchParams = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null> = {},
        sorter: SorterResult<VerificationVm> | SorterResult<VerificationVm>[] = {}
    ): URLSearchParams => {
        const filterParamsString = getFilterParamsString(filters);
        const singleSorter = sorter as SorterResult<VerificationVm>;
        const tableSearchParams = new URLSearchParams(filterParamsString);

        tableSearchParams.append('pageIndex', pagination.current?.toString() || '1');
        tableSearchParams.append(
            'pageSize',
            pagination.pageSize?.toString() || pagination.defaultPageSize?.toString() || ''
        );
        tableSearchParams.append(
            'sortBy',
            singleSorter.field?.toString() && singleSorter.order
                ? singleSorter.field?.toString()
                : ''
        );
        tableSearchParams.append('sortOrder', singleSorter.order || '');

        return tableSearchParams;
    };

    private getVerificationTableStatusBadge = (
        status: VerificationStatusEnum,
        text?: string
    ): React.ReactElement => {
        switch (status) {
            case VerificationStatusEnum.Unverified:
                return <Badge status="processing" text={text} />;
            case VerificationStatusEnum.Verified:
                return <Badge status="success" text={text} />;
            case VerificationStatusEnum.Rejected:
                return <Badge status="error" text={text} />;
            default:
                return <></>;
        }
    };

    public render(): React.ReactElement {
        const { onUpdate, onDelete } = this.props;
        const { allowUpdate, allowDelete, statuses, loading } = this.state;
        const { location, verifications } = this.props;

        const params = new URLSearchParams(location.search);
        const { sortByParam, sortOrderParam, pageIndexParam, pageSizeParam } =
            getPagingSortingParams(params);
        const filters = getFiltersParams(params);
        const pagination: TablePaginationConfig = {
            ...defaultTablePagination,
            current:
                pageIndexParam && parseInt(pageIndexParam)
                    ? parseInt(pageIndexParam)
                    : defaultTablePagination.defaultCurrent,
            pageSize:
                pageSizeParam && parseInt(pageSizeParam)
                    ? parseInt(pageSizeParam)
                    : defaultTablePagination.defaultPageSize,
        };

        const columns: ColumnsType<VerificationVm> = [
            {
                title: translations.verifications.fileName,
                dataIndex: 'fileName',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('fileName'),
                sorter: getDefaultComparer('fileName'),
                filteredValue: filters.fileName ? [filters.fileName] : null,
                sortOrder: sortByParam === 'fileName' ? sortOrderParam : null,
                render: (value: string, item: VerificationVm): React.ReactElement => {
                    return (
                        <Button
                            type="link"
                            onClick={async () => await this.openAttachmentInNewTab(item.id)}
                        >
                            {value}
                        </Button>
                    );
                },
            },
            {
                title: translations.verifications.project,
                dataIndex: 'project',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('project'),
                sorter: getDefaultComparer('project'),
                filteredValue: filters.project ? [filters.project] : null,
                sortOrder: sortByParam === 'project' ? sortOrderParam : null,
            },
            {
                title: documents.purchaseReceivedNote.name,
                dataIndex: 'documentCode',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('documentCode'),
                sorter: getDefaultComparer('documentCode'),
                filteredValue: filters.documentCode ? [filters.documentCode] : null,
                sortOrder: sortByParam === 'documentCode' ? sortOrderParam : null,
                render: (value: string, item: VerificationVm): React.ReactElement => {
                    return item.documentId ? (
                        <Link
                            to={{
                                pathname: Routes.ROUTE_DOCUMENTS_READ.replace(
                                    ReplaceStrings.ENTITY_ID,
                                    item.documentId.toString()
                                ),
                                search: `?typeId=${DocumentTypeEnum.PurchaseReceivedNote}`,
                            }}
                        >
                            {value}
                        </Link>
                    ) : (
                        <></>
                    );
                },
            },
            {
                title: translations.verifications.status,
                dataIndex: 'verificationStatusId',
                ...getCheckboxFilter(statuses),
                onFilter: getKeyFilter('verificationStatusId'),
                sorter: getDefaultComparer('verificationStatus'),
                filteredValue: filters.verificationStatusId
                    ? (filters.verificationStatusId as unknown as string).split(',')
                    : null,
                sortOrder: sortByParam === 'verificationStatusId' ? sortOrderParam : null,
                render: (
                    value: VerificationStatusEnum,
                    record: VerificationVm
                ): React.ReactElement =>
                    this.getVerificationTableStatusBadge(value, record.verificationStatus),
            },
            {
                title: translations.verifications.remark,
                dataIndex: 'remark',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('remark'),
                sorter: getDefaultComparer('remark'),
                filteredValue: filters.remark ? [filters.remark] : null,
                sortOrder: sortByParam === 'remark' ? sortOrderParam : null,
            },
            {
                title: translations.attachments.createdBy,
                key: 'createdBy',
                dataIndex: 'createdBy',
                ...getSearchFilter(),
                onFilter: getDefaultFilter('createdBy'),
                sorter: getDefaultComparer('createdBy'),
                filteredValue: filters.createdBy ? [filters.createdBy] : null,
                sortOrder: sortByParam === 'createdBy' ? sortOrderParam : null,
            },
            {
                title: translations.attachments.createdDate,
                key: 'createdDate',
                dataIndex: 'createdDate',
                width: 150,
                ...getDatePickerFilter(),
                onFilter: getDateFilter('createdDate'),
                filteredValue: filters.createdDate ? [filters.createdDate] : null,
                sorter: getDateComparer('createdDate'),
                sortOrder: sortByParam === 'createdDate' ? sortOrderParam : null,
                render: (value: Date) => formatDate(value, defaultFormatWithTime),
            },
            {
                title: translations.general.actions,
                key: 'actions',
                width: 100,
                align: 'left',
                className: 'no-print',
                render: (value: VerificationVm): React.ReactElement => (
                    <>
                        <DownloadOutlined
                            onClick={() => this.downloadAttachment(value.id)}
                            className="blue-6"
                        />
                        {allowUpdate && (
                            <EditOutlined
                                onClick={() => onUpdate(value.id)}
                                style={{ marginLeft: 10 }}
                            />
                        )}
                        {allowDelete && (
                            <DeleteOutlined
                                onClick={() =>
                                    showConfirm(
                                        () => onDelete(value.id),
                                        translations.verifications.deleteConfirm,
                                        translations.general.delete,
                                        true
                                    )
                                }
                                className="red-5"
                                style={{ marginLeft: 10 }}
                            />
                        )}
                    </>
                ),
            },
        ];

        return (
            <Table
                locale={getTableLocale()}
                columns={columns}
                dataSource={verifications}
                loading={loading}
                rowKey={(record: VerificationVm): string => record.id?.toString()}
                onChange={this.handleTableChange}
                pagination={pagination}
                bordered
                size="small"
            />
        );
    }
}

export default VerificationTable;
